// MA-C15 (ADR-067) — multi-bar fraction-comparison renderer.
//
// Draws ONE bar per answer option (already shuffled by the mapper, so the
// answer's position is random) so a fraction-comparison item ("Tap the
// biggest share.") gets a faithful picture instead of the ADR-021
// suppression (the single-bar renderer could only draw the ANSWER fraction,
// which revealed it). Every bar shares identical width/scale/styling — none
// is highlighted, labeled, or drawn differently — so the child must
// read+compare the shaded proportions themselves.
//
// Registered under "fraction_comparison" (see rendererRegistry). Reads
// `fractions`: an array of {a,b} numerator/denominator pairs, one per
// option, in the SAME order as the mapped item's `options` array.
//
// FAIL-SAFE: a missing/empty `fractions` array renders nothing (null) rather
// than a blank/garbled shape. Each pair is clamped exactly like
// FractionBarRenderer (denominator 1..MAX_DENOM, numerator 0..denominator)
// so a malformed pair degrades to an unshaded whole instead of NaN.
//
// MAX_DENOM is 16, not FractionBarRenderer's 12 — the real comparison corpus
// (ms.math.23/28) authors sixteenths (e.g. ME_M23_015's "1/16" distractor);
// clamping to 12 would silently redraw that bar as 1/12, a fidelity bug a
// red-team pass caught by sweeping the real corpus (not a fixture). Keep
// this in lockstep with renderabilityAudit.mjs's fraction_comparison
// contract upper bound — a corpus item authored past MAX_DENOM must fail the
// audit loudly, not silently mis-render.
import type { MathRendererProps } from "./rendererRegistry";

const SHADED = "var(--pal-green, #5a9e6f)";
const EMPTY = "var(--color-bg-secondary, #f5efe3)";
const STROKE = "var(--color-border-secondary, #cdbfae)";

const MAX_DENOM = 16; // clamp: keeps segment count + viewBox sane; matches renderabilityAudit's contract bound
const BAR_W = 280; // px — a touch narrower than the single-bar's 320 so several stacked bars still fit one screen
const BAR_H = 36; // px per bar
const ROW_GAP = 12; // vertical gap between bars
const PAD = 2; // outer padding so the stroke isn't clipped

function FractionComparisonRenderer({ fractions }: MathRendererProps) {
  const bars = (fractions ?? []).map(({ a, b }) => {
    const denom = Number.isFinite(b) ? Math.max(1, Math.min(MAX_DENOM, Math.floor(b))) : 1;
    const num = Number.isFinite(a) ? Math.max(0, Math.min(denom, Math.floor(a))) : 0;
    return { num, denom };
  });
  if (bars.length === 0) return null;

  const rowH = BAR_H + ROW_GAP;
  const height = bars.length * rowH - ROW_GAP + PAD * 2;

  return (
    <svg
      viewBox={`0 0 ${BAR_W + PAD * 2} ${height}`}
      width="100%"
      style={{ maxWidth: Math.min(320, BAR_W + PAD * 2) }}
      role="img"
      aria-label={`${bars.length} fraction bars to compare`}
    >
      {bars.map(({ num, denom }, i) => {
        const segW = BAR_W / denom;
        const y = PAD + i * rowH;
        return (
          <g key={i}>
            {Array.from({ length: denom }).map((_, j) => (
              <rect
                key={j}
                x={PAD + j * segW}
                y={y}
                width={segW}
                height={BAR_H}
                fill={j < num ? SHADED : EMPTY}
                stroke={STROKE}
                strokeWidth="2"
              />
            ))}
          </g>
        );
      })}
    </svg>
  );
}

export default FractionComparisonRenderer;
